// @ts-strict
// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## InteractivePrototype.js
//
// A basic class for all InteractiveObjects
/* eslint class-methods-use-this: "off" */

import * as Mousetrap from 'mousetrap';
import type { UserPreferences, Accommodation } from 'shared/definitions/user-object-definitions';
import type { UserSkillStore } from 'shared/src/user/userKnowledge/user.skill.store';
import type ClientUserStore from 'shared/src/user/client.user.store';
import type { Skill } from 'definitions/skill-definitions';
import type { UPracticeData } from 'shared/definitions/progress-data';
import { isObject } from 'shared/src/utils/isObject';
import { isNumber } from 'shared/src/utils/isNumber';
import { wait } from 'shared/src/utils/wait';
import utils from '../utils';
import UPrefs from './uPrefs';
// import * as Notation from '../notation';
import PianoInstrument from '../uTheory.Notation/pianoInstrument';
import type { RendererOptions } from '../uTheory.Notation/renderer';
import Renderer from '../uTheory.Notation/renderer';
import type { EditorOptions } from '../uTheory.Notation/editor';
import Editor from '../uTheory.Notation/editor';
import type Music from '../uTheory.Notation/music';
import PianoSVG from './PianoSVG/pianosvg';
import CheckMark from './checkMark';
import ProgressTracker from '../uTheory.Skills/progressTracker';
import type { LetterPanelOptions } from '../uTheory.Notation/letterPanel';
import LetterPanel from '../uTheory.Notation/letterPanel';
import type { SizeDivOptions } from './sizeDiv';
import sizeDiv from './sizeDiv';
import * as Skills from '../uTheory.Skills/skills';
import type { AnswerAndData } from './interactiveQuestionPrototype';
// import { UserData } from '../uTheory.APITypes/api-types';
import errorProvider from '../errorProvider';
import UApi from './uApi';
import type { InstructionsOptions, PriorInstructions } from './instructionsDivUpdater';
import { displayInstructions } from './instructionsDivUpdater';

export interface UserData {
  skills: UserSkillStore[];
  preferences: UserPreferences;
  progress: UserSkillStore[];
  accommodation: Pick<Accommodation, 'time' | 'accuracy'>;
  userStore: ClientUserStore;
}

const debugMode = false;
let mousetrapLastResetBy: boolean | InteractivePrototype = false;
const debug = debugMode ? console.log.bind(console) : (): void => {};

export interface InteractiveOptions {
  questions?: any[];
  testMode?: boolean;
  drill?: boolean;
  skills?: (number | string)[];
  maxQuestions?: number;
  questionPrompt?: string;
}

// Define types for various settings properties:
interface InstructionsPanelOptions {
  innerHTML?: string;
  /** The start button calls this function */
  callback?: Function;
  /** thisArg for the start button callback */
  thisArg?: any;
  /** Label for the start button, default 'start' */
  buttonLabel?: string; // default "start"
  /** Function to call when everything is loaded... */
  onInteractiveReady?: (
    textDiv: HTMLDivElement,
    buttonDiv: HTMLDivElement,
    button: HTMLButtonElement
  ) => any;
  /** Once the splash creen has been initialized, before the loading is done: */
  onSplashScreen?: (textDiv: HTMLDivElement, buttonDiv: HTMLDivElement) => any;
}

export interface QuestionDataOptions {
  // pass an object of XHR info to load:
  filename: string;
  directory?: string; // relative to root... or http:// for global.
  type?: string; // default "json" (etc...)
}

interface UPrefsOptions {
  appName: string;
  optional: boolean;
}

interface CheckSettings {
  rightAnswerLength?: number;
  rightAnswerInOutRatio?: number;
  wrongAnswerLength?: number;
  wrongAnswerInOutRatio?: number;
  div?: HTMLDivElement;
}

export interface InteractiveSettings {
  /** For more complex instructions that should be displayed as a full-page instruction set at the start of an interactive */
  instructionsPanel?: false | InstructionsPanelOptions;
  /** user prefs & history for an interactive. */
  uPrefs?: false | UPrefsOptions;
  /** Load questions from the server/database? */
  questionData?: false | QuestionDataOptions;

  // Displayed elements of interactives:
  /** Whether to display an instruction, from questionPrompt, at top of interactive */
  instructions?: boolean | InstructionsOptions;
  /** An editor for musical notation (interactive) */
  editor?: false | Partial<EditorOptions>;
  /** A renderer of musical notation (not interactive) */
  notation?: false | Partial<RendererOptions>;
  /** A piano keyboard for user input or displaying notes */
  piano?: boolean | object;
  /** Letters, accidentals, and checkButton can all be initialized via a lettersPanel, or use accidentals & checkButton for convenience,
   * which will still initialize these via a lettersPanel
   */
  letters?: false | Partial<LetterPanelOptions>;
  /** Whether to display a checkButton (via a lettersPanel) */
  checkButton?: boolean | CheckSettings;
  /** Whether to display a flashing green check or red X when a user answers */
  checkMark?: boolean | object;
  /** Whether to include a PianoInstrument as interactive.player */
  player?: boolean | object;
  /** Whether a user should have only one or more tries at answering each question */
  oneTryOnly?: boolean; // true: go on to next question even if prior was wrong.
  /** Whether to include a ProgressTracker (almost always true) */
  progressTracker?: boolean;
  /** Whether to load audio before presenting questions */
  loadAudioFirst?: boolean;
}

// Displayed elements of interactives:
// Settings are game object / design settings that apply to all
// interactives. The defaults are set here.
//
// They specify what gameplay elements will be present and how they will be
// laid out. The default settings are here, they are overriden during
// inheritence.
//
// See InteractiveBlank.js for examples of this.

const settings: InteractiveSettings = {
  // Related to loading user data & question banks:
  instructionsPanel: false, // for more complex instructions that
  // should be displayed before a user starts the game.
  /* {  innerHTML: string,
          callback: callback,
          thisArg: for the callback,
          buttonLabel: string, default "start"
        } */
  uPrefs: false, // user prefs & history for an app.
  // set to the appName for the Parse.uPrefs class.
  questionData: false, // pass an object of XHR info to load:
  /* { filename : string,
         directory : string, // relative to root... or http:// for global.
         type: "json" (etc...)
       }
    */
  // Displayed elements of interactives:
  instructions: false,
  editor: false, // An editor for musical notation.
  notation: false,
  piano: false,
  letters: false, // Letters, accidentals, and checkButton can all be
  // initialized via letters if desired... it's all in the same
  // object. Or use accidentals & checkButton for convenience.
  checkMark: {},
  player: {},

  // Interaction elements -- how things like "wrongAnswer" behave by
  // default:
  oneTryOnly: false, // true: go on to next question even if prior was wrong.
  progressTracker: true, // track progress, send to Parse for saving
};

export interface LayoutItem {
  name?: string;
  percent?: number;
  visible?: boolean;
  style?: object;
  div?: HTMLDivElement;
  /**
   * Resize div within the available height, return the unused height in pixels.
   */
  callback?: (availableHeight: number, div: HTMLDivElement) => number;
  noWrap?: boolean;
  overlay?: boolean;
  /** Once we're done, should we assign any leftover height to this? */
  greedy?: boolean;
  /** What's the largest height percent we should let this have? */
  maxPercent?: number;
}

export interface LayoutSettings {
  /** Layout for full-page instructions before start of interactive */
  instructionsPanel?: LayoutItem;
  /** Layout for instructions at top of interactive */
  instructions?: LayoutItem;
  /** Layout for musical notation edtior */
  editor?: LayoutItem;
  /** Layout for musical notation renderer (non-interactive) */
  notation?: LayoutItem;
  /** Layout for piano keyboard */
  piano?: LayoutItem;
  /** Layout for buttons (letters is a misnomer) */
  letters?: LayoutItem;
  /** Layout for accidental buttons */
  accidentals?: LayoutItem;
  /** Layout for slider buttons */
  sliders?: LayoutItem;
  /** Place holder for a custom div */
  customDiv?: LayoutItem;
  /** Place holder for additional custom divs */
  [customLayoutItem: string]: LayoutItem | undefined;
}

type GameOverCallback = (data: UPracticeData[] | undefined) => any;
type AnswerAndDataCallback = (data: AnswerAndData) => any;

export default class InteractivePrototype {
  // Uninitialized properties:
  displayDiv: HTMLDivElement;

  options: InteractiveOptions;

  userData?: UserData;

  settings: InteractiveSettings;

  setDebugMode: boolean;

  /**
   * in seconds, used to report live progress during interactive
   */
  targetSpeed?: number;

  /** skill ids to calculate targetSpeed (above) */
  targetSpeedFromSkills?: (number | string)[];

  private resizeIndex?: number; // to clear Window.onResize callbacks.

  currentQuestion: object | any; // the current question object

  questions!: (object | any)[];

  questionData: any; // data loaded from XHR query to server for generating questions.

  layout!: LayoutSettings;

  music?: Music;

  questionPrompt?: string | Function;

  currentInstructions?: string;

  tempo?: number;

  private priorInstructions?: PriorInstructions;

  // Initialized properties:
  rightAnswerCallbacks: Function[] = [];

  wrongAnswerCallbacks: Function[] = [];

  gameOverCallbacks: GameOverCallback[] = [];

  questionNumber = 0;

  wrongAnswerCount = 0;

  checkpointAnswerCallbacks: AnswerAndDataCallback[] = [];

  loadingPromise!: Promise<any>;

  uPrefsLoadingPromise!: Promise<any>;

  questionDataLoadingPromise!: Promise<any>;

  audioLoadingPromise!: Promise<any>;

  rightAnswerDelay = false; // toggles based on whether presentation of the

  // next question is being delayed based on whether a user just made a correct
  // answer; prevents accidental double submissions.
  testMode = false;

  // Optional properties -- determined by settings:
  progressTracker?: ProgressTracker;

  uPrefs?: UPrefs;

  player?: PianoInstrument;

  checkMark?: CheckMark;

  instructionsDiv?: HTMLDivElement;

  piano?: PianoSVG;

  pianoDiv?: HTMLDivElement;

  editor?: Editor;

  editorDiv?: HTMLDivElement;

  letterPanel?: LetterPanel;

  lettersDiv?: HTMLDivElement;

  instructionsPanelDiv?: HTMLDivElement;

  private instructionsDivHeight?: number;

  notation?: Renderer;

  private notationDiv?: HTMLDivElement;
  /** Timeout integer for applying greedy layout */
  // private greedyTimeout?: number;

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions = {}, userData?: UserData) {
    console.debug('starting interactive', this.constructor.name);
    debug('starting interactive', this.constructor.name);
    this.displayDiv = displayDiv;
    this.options = utils.clone(options, true);
    this.userData = userData;
    this.settings = utils.clone(settings);
    this.setDebugMode = debugMode;
    this.targetSpeedFromSkills = undefined; // array of skills -- translated to targetSpeed
    this.targetSpeed = undefined; // in seconds
  }

  debug(...args: any[]): void {
    debug(...args);
  }

  // Public Functions

  init(displayDiv: HTMLDivElement, options: InteractiveOptions, userData?: UserData): Promise<any> {
    // Make display div position relative, so that check mark can reside within it.
    displayDiv.style.position = 'relative';

    debug('initializing interactive');
    this.options = options;
    if (userData) this.userData = userData;

    // GCR: Debug
    (window as any).interactive = this;

    // Let's disable any other Mousetrap listeners:
    Mousetrap.reset();
    mousetrapLastResetBy = this;

    // just be sure displayDiv is thoroughly empty:
    while (displayDiv.hasChildNodes() && displayDiv.lastChild) {
      displayDiv.removeChild(displayDiv.lastChild);
    }

    // We do this now, so that loading can start as soon as possible.
    if (this.settings.player) this.initializePlayer();

    // if this.targetSpeedFromSkills is set, calculate our target speed:
    this.setTargetSpeed();

    this.initializeLayout();

    // Options are specific to an implementation, and refer to things like
    // question lists, etc...
    this.options = typeof options !== 'undefined' ? options : {};

    this.displayDiv = displayDiv;

    // displayDiv.style.height = '100%';

    // Clear any utils.onResize -- in case one remains from a previous
    // interactive loaded in this window:
    utils.clearResize();

    // And set up to call this back if resized:
    this.resizeIndex = utils.onResize(this.resize, this);

    // Global Variables:
    this.rightAnswerCallbacks = [];
    this.wrongAnswerCallbacks = [];
    this.gameOverCallbacks = [];
    this.questionNumber = 0;

    this.checkpointAnswerCallbacks = [];

    // Initialize the layout, etc...
    // If we need to load user preferences & or question data,
    // do that first...
    const prefsAndDataPromise = this.loadPrefsAndData();
    if (this.settings.instructionsPanel) this.splashInstructionsPanel();
    this.loadingPromise = prefsAndDataPromise
      .then(() => this.finishInit())
      .catch((err) => {
        console.log('loading promise error!');
        errorProvider.error(err);
        console.error(err);
        this.loadError(err);
        return Promise.reject(err);
      });
    return this.loadingPromise;
  }

  splashInstructionsPanel(): void {
    this.instructionsPanelDiv = this.createInstructionsPanelDiv();
    this.initializeInstructionsPanel();
    this.instructionsPanelDiv.setAttribute('aria-modal', 'true');
    this.displayDiv.innerHTML = '';
    this.displayDiv.appendChild(this.instructionsPanelDiv);
    this.resize();
  }

  finishInit(): Promise<void> {
    const finishInitPromise = new Promise<void>((resolve) => {
      // console.log('finishing init');
      // If we're not in testMode, genereate the questions.
      this.initializeMusic();
      if (!this.optionsTrue('testMode')) {
        // console.log('calling generateQuestions');
        this.generateQuestions();
        this.currentQuestion = this.questions[0];
      }

      // clear div:
      if (!this.settings.instructionsPanel) this.displayDiv.innerHTML = '';

      this.createDivs();
      this.initializeElements();
      if (this.settings.instructionsPanel && this.instructionsPanelDiv) {
        this.displayDiv.removeChild(this.instructionsPanelDiv);
        this.displayDiv.appendChild(this.instructionsPanelDiv);
      }
      if (this.settings.progressTracker) this.progressTracker = new ProgressTracker();

      // Don't enable listeners until after instructionsPanel is dismissed:
      if (!this.settings.instructionsPanel) {
        this.addListeners();
      }

      // If we're not in testMode, display the first question.
      if (!this.optionsTrue('testMode')) {
        this.newQuestion();
      }
      window.setTimeout(() => {
        // Force a div resize after music's loaded:
        utils.windowResizeCallback();
      }, 0);
      resolve(undefined);
    });

    return finishInitPromise;
  }

  loadPrefsAndData(): Promise<void> {
    debug('Loading prefs!');

    this.loadUPrefs();
    this.loadQuestionData();
    this.loadAudio();

    // if (!this.settings.instructionsPanel)
    this.updateLoadProgress(this.displayDiv);
    return Promise.all([
      this.uPrefsLoadingPromise,
      this.questionDataLoadingPromise,
      this.audioLoadingPromise,
    ])
      .then(() => {
        debug('uPrefs:');
        debug(this.uPrefs);
        debug('questionData:');
        debug(this.questionData);
      })
      .catch((e) => {
        this.loadError(e);
      });
  }

  loadUPrefs(): Promise<void> {
    // Load the prefs:
    let uPrefsPromise = Promise.resolve();
    if (this.settings.uPrefs) {
      debug('loading uPrefs');
      this.uPrefs = new UPrefs();
      uPrefsPromise = this.uPrefs.setAppName(this.settings.uPrefs.appName).catch((err) => {
        debug('Error loading uPrefs');
        if (this.settings.uPrefs && this.settings.uPrefs.optional) {
          console.warn(err);
          return Promise.resolve();
        }
        errorProvider.error('Error loading uPrefs', err);
        console.error(err);
        return Promise.reject(err);
      });
    }

    this.uPrefsLoadingPromise = uPrefsPromise;
    if (!this.settings.uPrefs || this.settings.uPrefs.optional) {
      return Promise.resolve();
    }
    return uPrefsPromise;
  }

  loadQuestionData(): Promise<void> {
    let questionDataPromise = Promise.resolve();
    // Load the questionData:
    if (this.settings.questionData && !this.optionsTrue('manualQuestions')) {
      debug('fetching questionData');
      const url =
        (this.settings.questionData.directory || '') + this.settings.questionData.filename;
      const uApi = new UApi(url);
      questionDataPromise = uApi
        .get()
        .then((questionData) => {
          this.questionData = questionData;
        })
        .catch((err) => {
          console.error('Failed to load question data', err);
          if (err?.statusText) {
            errorProvider.error('Error loading question data', err.statusText);
          } else {
            errorProvider.error('Error loading question data', err);
          }
          debug('Failed to load question data', err);
          return Promise.reject(err);
        });
    }
    this.questionDataLoadingPromise = questionDataPromise;
    return questionDataPromise;
  }

  loadAudio(): Promise<void> {
    // If audio has to be loaded for this interactive, let's
    // load it first...
    //
    // Note, you can also hide this in an instructionPanel,
    // by overriding the showStartButton method to respond to
    // the buffer loading. See, for example, oneNoteSolfege.js
    //
    let audioLoadedPromise = Promise.resolve();
    if (this.settingSet('loadAudioFirst')) {
      audioLoadedPromise = new Promise(
        // eslint-disable-next-line arrow-body-style
        (resolve, reject) => {
          debug('loading audio first');
          if (!this.player) {
            errorProvider.error('Error loading audio, no player!');
            console.error('Error loading audio, no player!');
            this.loadError();
            reject('No player!');
          }

          let loaded = false;
          const trackLoadStatus = (percent: number) => {
            if (!loaded && percent === 100) {
              loaded = true;
              resolve();
            }
          };

          this.player!.buffer.notifyLoadStatus(trackLoadStatus);
          this.player!.buffer.notifyError((err: unknown) => {
            console.error('error provider alerting to error loading audio');
            errorProvider.error('Error loading audio', err);
            reject(err);
          });
        }
      );
    }
    this.audioLoadingPromise = audioLoadedPromise;

    return audioLoadedPromise;
  }

  loadError(err?: unknown): void {
    if (this.instructionsPanelDiv) {
      this.instructionsPanelDiv.removeAttribute('aria-modal');
      this.instructionsPanelDiv.style.pointerEvents = 'none';
      this.instructionsPanelDiv.style.transition = 'none';
      this.instructionsPanelDiv.style.opacity = '0';
      this.instructionsPanelDiv.style.display = 'none';
    }

    this.displayDiv.innerHTML =
      '<p>Sorry, something went wrong. We ' +
      "weren't able to load questions or your history for this exercise. " +
      'Please try reloading this page, checking your internet connection,' +
      " and if that doesn't work, please let us know about the problem.</p>" +
      '<p>Thanks!</p><p>Your uTheory team</p>';
    if (isObject(err) && 'stack' in err) console.error(err);
    throw err instanceof Error ? err : new Error(err as string);
  }

  prefOrDataLoaded(): void {
    // Check the status of either questionData or
  }

  // Used for laying out the relative sizes of each part:
  initializeLayout(): void {
    debug('initializing layout');
    // create falsies for missing settings:
    const divs = ['instructions', 'editor', 'notation', 'piano', 'letters', 'checkButton'] as const;
    divs.forEach((divName) => {
      // eslint-disable-next-line no-prototype-builtins
      if (!this.settings.hasOwnProperty(divName)) {
        this.settings[divName] = false;
      }
    }, this);

    const appSpecificLayout = this.layout;

    const defaultLayout = {
      instructionsPanel: {
        name: 'instructionsPanel',
        percent: 0,
        visible: false,
      },
      instructions: {
        name: 'instructions',
        percent: 15,
        visible: false,
      },
      editor: {
        name: 'editor',
        percent: 65,
        visible: false,
      },
      notation: {
        name: 'notation',
        percent: 65,
        visible: false,
      },
      piano: {
        name: 'piano',
        percent: 65,
        visible: false,
      },
      letters: {
        name: 'letters',
        percent: 20,
        visible: false,
      },
      accidentals: {
        name: 'accidentals',
        percent: 20,
        visible: false,
      },
    };

    // Merge custom settings in...
    if (typeof appSpecificLayout !== 'undefined') {
      utils.merge(defaultLayout, appSpecificLayout, true);
    }
    this.layout = defaultLayout;
  }

  private resizeCallIndex = 0;

  async resize(): Promise<void> {
    const resizeCallIndex = ++this.resizeCallIndex;
    await wait(0.1);
    if (resizeCallIndex !== this.resizeCallIndex) return;
    const height = this.getAvailableHeight();
    // Splash screen might set this -- remove it!
    this.displayDiv.style.height = '';

    debug(`Interactive resizing to: ${height}`);

    // Determine which div's we're considering:
    const divs: (LayoutItem & {
      percent: number;
      div: HTMLDivElement;
      callback: (height: number, div: HTMLDivElement) => void;
    })[] = [];
    const overlays: (LayoutItem & {
      visible: true;
      div: HTMLDivElement;
      callback: (height: number, div: HTMLDivElement) => number;
    })[] = [];
    let totalPercent = 0;
    Object.keys(this.layout).forEach((key: keyof typeof this.layout) => {
      const layoutItem: LayoutItem | undefined = this.layout[key];
      if (!layoutItem) return;
      debug('setting up to resize', key);
      debug(layoutItem);
      // If it's not visible, hide it from the dom:
      if (!layoutItem.visible && layoutItem.div) {
        layoutItem.div.style.display = 'none';
      }
      if (layoutItem.overlay && layoutItem.visible && layoutItem.div && layoutItem.callback) {
        overlays.push(
          layoutItem as LayoutItem & {
            visible: true;
            div: HTMLDivElement;
            callback: (height: number, div?: HTMLDivElement) => number;
          }
        );
      }
      if (layoutItem.visible && layoutItem.div && !layoutItem.overlay) {
        layoutItem.div.style.display = 'block';
        debug(`added ${key} to the queue!`);
        const percent = layoutItem.percent || 20;
        const callback: LayoutItem['callback'] =
          layoutItem.callback ||
          ((availableHeight: number) =>
            sizeDiv(layoutItem.div!.clientWidth, availableHeight, layoutItem.div!));
        divs.push({
          ...layoutItem,
          div: layoutItem.div,
          percent,
          callback,
        });
        totalPercent += percent;
      }
    });
    debug(`Total percent: ${totalPercent}`);

    if (!divs.length && !overlays.length) return;

    // Distribute the available height among our divs:
    /** the total height available, period the end. */
    let availableHeight = height;
    /** height that's been used by elements that are maxed out */
    let maxedOutHeight = 0;
    /** Heights assigned in this round */
    let heightUsedThisRound = 0;
    let rounds = 0;

    /** Left over height to be assigned this round if possible, or next round */
    let extraHeight = height;
    while (
      availableHeight > 1 &&
      extraHeight > 5 &&
      //        rounds < 2 &&
      //        (availableHeight - heightUsedThisRound) > 2 &&
      divs.length !== 0 &&
      rounds < 40
    ) {
      rounds++;
      if (rounds === 20) {
        console.warn('Resizing error! 20 rounds!');
      }
      if (rounds === 39) {
        console.warn('Resizing stopped from infinite loop!');
        console.log('divs', divs);
      }

      debug(
        `\n\n\n=======================================
          \n\n\nROUND ${rounds}
          \n\n\n=======================================
          \n\ndivs.length: ${divs.length}`
      );
      debug(`height this round: ${availableHeight}`);

      let maxedOutPercent = 0;
      heightUsedThisRound = 0;
      // Resize each div, tracking left over height:
      for (let i = 0; i < divs.length; i++) {
        const el = divs[i];

        // If el doesn't exist, error -- this should never happen
        if (!el) {
          throw Error(`divs[${i}] is not a LayoutItem!`);
        }

        // if it's a overlay div, let it resize itself at full height:
        // and then pull it out of the equation.
        if (el.overlay && el.callback) {
          debug(`Resizing: ${el.name}`);
          el.callback(height, el.div);
          divs.splice(i--, 1);
          // eslint-disable-next-line no-continue
          continue;
        }

        // otherwise, size as normal:
        const divHeight = ~~((el.percent * availableHeight) / totalPercent);
        const maxDivHeight = el.maxPercent && ~~((el.maxPercent * height) / 100);
        const offeringHeight = el.maxPercent ? Math.min(divHeight, maxDivHeight!) : divHeight;
        const maxUsed = el.maxPercent && maxDivHeight! <= divHeight;
        debug(
          `Resizing: ${el.name}
           Total height: ${height}
           totalPercent: ${totalPercent}
            Available height: ${availableHeight}
            Available width: ${this.displayDiv.clientWidth}
            element maxPercent: ${el.maxPercent || 'none'}
            element percent: ${el.percent}
            offering height: ${offeringHeight}
            used maxPercent?: ${maxUsed}
            maxedOutPercent: ${maxedOutPercent}`
        );

        debug('el', el);
        el.div.style.height = `${offeringHeight}px`;
        let leftOver = el.callback(offeringHeight, el.div);
        if (typeof leftOver === 'undefined') {
          const elUsed = el.div.offsetHeight;
          leftOver = offeringHeight - elUsed;
        }

        heightUsedThisRound += el.div.offsetHeight;
        debug(`${el.name} used ${el.div.offsetHeight}`);

        if (maxUsed || leftOver >= 1) {
          debug(`${el.name} is maxed out at ${el.div.offsetHeight}`);
          maxedOutPercent += el.percent;
          maxedOutHeight += el.div.offsetHeight;

          divs.splice(i--, 1);
        } else {
          debug(`${el.name} used ${el.div.offsetHeight}`);
        }
      }

      // Make our available height whatever's left over:
      availableHeight = height - maxedOutHeight;
      extraHeight = height - maxedOutHeight - heightUsedThisRound;
      totalPercent -= maxedOutPercent;

      debug(
        `

========= END OF ROUND =========
totalHeight: ${height}
heightUsedThisRound: ${heightUsedThisRound}
maxedOutHeight: ${maxedOutHeight}
availableHeight: ${availableHeight}
extraHeight: ${extraHeight}
`
      );
    } // end while...

    const layoutItems: LayoutItem[] = Object.keys(this.layout)
      .map((key) => this.layout[key])
      // Be sure it's visible and has a div -- may not have div if we're still on a loading screen.
      .filter((item): item is LayoutItem => !!item && !!item.visible && !!item.div);
    const heightUsed = layoutItems.reduce((sum, item) => sum + item.div!.clientHeight, 0);

    // if (this.greedyTimeout) window.clearTimeout(this.greedyTimeout);
    // this.greedyTimeout = window.setTimeout(() => {
    // If anything is greedy, give it extra room...
    const greedyItems: (LayoutItem & { div: HTMLDivElement })[] = Object.keys(this.layout)
      .map((key) => this.layout[key])
      .filter((item): item is LayoutItem & { div: HTMLDivElement } => !!item && !!item.div)
      .filter((layoutItem) => layoutItem.visible && layoutItem.greedy);
    debug('Greedy items', greedyItems);
    if (greedyItems.length && height - heightUsed > 20) {
      const heightAvailableForGreedyItems = height - heightUsedThisRound - 20;
      const extraHeightForGreedyItems = heightAvailableForGreedyItems / greedyItems.length;
      greedyItems.forEach((item) => {
        const currentHeight = item.div.clientHeight;
        item.div.style.height = `${currentHeight + extraHeightForGreedyItems}px`;
      });
      debug(`greedyHeightUsed: ${heightAvailableForGreedyItems}`);
    }
    // }, 10);

    // The checkMark should be set to fill the used space:
    if (this.settings.checkMark && this.checkMark) {
      debug('Resizing check mark:');
      this.checkMark.resize();
      debug('Done resizing checkmark.');
    }

    overlays.forEach((overlay) => {
      this.displayDiv.style.height = `${height}px`;
      overlay.callback(height, overlay.div);
      overlay.div.style.height = `${height}px`;
      overlay.div.style.width = `${this.displayDiv.clientWidth}px`;
    });

    debug('\n\n RESIZE FINISHED \n\n');
  }

  sizeTextDiv(height: number, div: HTMLDivElement, options?: Partial<SizeDivOptions>): number {
    const width = this.displayDiv.clientWidth;
    return sizeDiv(width, height, div, options);
  }

  getAvailableHeight(): number {
    // Determines max height for layout:
    const children = [];
    // Pull out any divs in our space to get accurate measurement:
    while (this.displayDiv.hasChildNodes() && this.displayDiv.lastChild) {
      children.push(this.displayDiv.removeChild(this.displayDiv.lastChild));
    }

    let availableHeight = 0;
    let rect;
    // If we're in a lesson, some things are pre-set for us because of
    // needed elements in the rest of the layout. If that's the case,
    // we'll be in uInteractiveDiv.

    if (document.getElementById('uInteractiveDiv') !== null) {
      // Start with the alotted min height from the css layout:
      rect = document.getElementById('uInteractiveDiv')!.getBoundingClientRect();
      availableHeight = rect.height;

      // Then see if there's more available from the bottom of the screen:
      const lowerButtonSpace = 70; // 70 px w/ height & padding.
      // Distance from bottom of rect to bottom of screen:
      let toScreenBottom = window.innerHeight - rect.bottom;
      // But if the top of the screen's out of view, save that much space:
      if (rect.top < 0) toScreenBottom += rect.top;
      // And subtract the button space:
      toScreenBottom -= lowerButtonSpace;
      // And if it's bigger than 0, add it to our available height:
      if (toScreenBottom > 0) availableHeight += toScreenBottom;
    } else {
      // Otherwise, look at VexDiv's bounding rectangle, and see how far away
      // the top of the div is from the bottom of the screen, and resize
      // accordingly:
      rect = this.displayDiv.getBoundingClientRect();
      const top = Math.abs(rect.top);
      const screenBottom = window.innerHeight;
      availableHeight = screenBottom - top - 5; // -5 for padding...
    }

    // Put any elements we took out back:
    children.reverse(); // so that we put them back in the right order...
    children.forEach((el) => {
      this.displayDiv.appendChild(el);
    });
    return availableHeight;
  }

  createDivs(): void {
    // Clean it out if there's something there:

    if (this.settings.instructions) this.createInstructionsDiv();
    if (this.settings.notation) this.createNotationDiv();
    if (this.settings.piano) this.createPianoDiv();
    if (this.settings.editor) this.createEditorDiv();
    if (this.settings.letters || this.settings.checkButton) this.createLettersDiv();
    this.createCustomDivs();
    // if (this.settings.instructionsPanel) this.createInstructionsPanelDiv();
  }

  createDiv(
    divName: string,
    options?: Partial<CSSStyleDeclaration>,
    doNotAppend = false
  ): HTMLDivElement {
    const styleOptions: Partial<CSSStyleDeclaration> = { width: '100%', ...options };
    this.stylePrefix(styleOptions);
    // @ts-ignore - GCR: This polutes the global with a divname at runtime, which effectively
    // makes static checking impossible... we would do well to remove this code in the future
    this[divName] = document.createElement('div');
    // @ts-ignore
    utils.merge(this[divName].style, styleOptions);
    //      this[divName].style.width = styleOptions.width;
    if (!doNotAppend) {
      // @ts-ignore
      this.displayDiv.appendChild(this[divName]);
    }
    // @ts-ignore
    return this[divName];
  }

  insertAfter(newNode: HTMLElement, referenceNode: HTMLElement): void {
    if (!referenceNode.parentNode) {
      throw Error('Cannot insert after an element that does not have a parent node.');
    }
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
  }

  stylePrefix(styleObject: Partial<CSSStyleDeclaration>): void {
    Object.keys(styleObject).forEach((prop) => {
      if (typeof document.body.style[prop as any] === 'undefined') {
        const newProp = `webkit${prop.charAt(0).toUpperCase()}${prop.slice(1)}`;
        if (typeof document.body.style[newProp as any] !== 'undefined') {
          // eslint-disable-next-line no-param-reassign
          styleObject[newProp as any] = styleObject[prop as any];
          // eslint-disable-next-line no-param-reassign
          delete styleObject[prop as any];
        }
      }
    });
  }

  createCustomDivs(): void {}

  // Default initialization routines
  // can be overriden to apply styles as needed
  createInstructionsDiv(): void {
    if (!this.layout.instructions) {
      throw Error('Cannot create instructions div unless this.layout.instructions is defined.');
    }
    const options = { fontSize: '2vw', textAlign: 'center' };
    if (this.layout.instructions.style) {
      utils.merge(options, this.layout.instructions.style);
    }

    // First we instert an H3 sr-only just before the instructions div:
    const h3 = document.createElement('h3');
    h3.textContent = 'Instructions';
    h3.classList.add('sr-only');
    this.displayDiv.appendChild(h3);
    this.instructionsDiv = this.createDiv('instructionsDiv', options);
    this.layout.instructions.div = this.instructionsDiv;
    this.layout.instructions.visible = true;
    this.layout.instructions.callback = (height: number, div: HTMLDivElement) => {
      const { noWrap } = this.layout.instructions!;
      const leftoverHeight = this.sizeTextDiv(height, div, { noWrap });
      this.instructionsDivHeight = this.instructionsDiv!.clientHeight;
      return leftoverHeight;
    };
  }

  createNotationDiv(): void {
    if (!this.layout.notation) {
      throw Error('Cannot create notation div if this.layout.notation is undefined.');
    }

    this.createDiv('notationDiv', {});
    this.layout.notation.div = this.notationDiv;
  }

  createPianoDiv(): void {
    if (!this.layout.piano) {
      throw Error('Cannot create piano div if this.layout.piano is undefined.');
    }
    //      var heightPercent = 100 * this.layout.piano / this.totalHeightParts;
    const options = {
      textAlign: 'center',
      'margin-left': 'auto',
      'margin-right': 'auto',
      //  flexGrow: 1
    };
    this.createDiv('pianoDiv', options);
    this.layout.piano.div = this.pianoDiv;
    const h3 = document.createElement('h3');
    h3.classList.add('sr-only');
    h3.textContent = 'Piano Keyboard';
    this.pianoDiv?.appendChild(h3);
  }

  createEditorDiv(): void {
    if (!this.layout.editor) {
      throw Error('Cannot create editor div if this.layout.editor is undefined.');
    }
    //      var heightPercent = 100 * this.layout.editor / this.totalHeightParts;
    //      var options = {height : heightPercent + "%"};
    const options = {
      textAlign: 'center',
      'margin-left': 'auto',
      'margin-right': 'auto',
      //        flexGrow : 1
    };
    this.createDiv('editorDiv', options);
    this.layout.editor.div = this.editorDiv;
  }

  createLettersDiv(): void {
    if (!this.layout.letters) {
      throw Error('Cannot create letters div if this.layout.letters is undefined.');
    }
    //      var heightPercent = 100 * this.layout.letters / this.totalHeightParts;
    const options = {
      textAlign: 'center',
      //        fontSize: "8vw",
      //        flexGrow: 0
    };
    this.createDiv('lettersDiv', options);
    this.layout.letters.div = this.lettersDiv;
    const h3 = document.createElement('h3');
    h3.classList.add('sr-only');
    h3.textContent = 'Answer Buttons';
    this.lettersDiv?.appendChild(h3);
  }

  createInstructionsPanelDiv(options?: Partial<CSSStyleDeclaration>): HTMLDivElement {
    if (!this.layout.instructionsPanel) {
      throw Error('Cannot initialize instructions panel if layout.instructionsPanel is undefined');
    }
    options = {
      position: 'absolute',
      top: '0px',
      left: '0px',
      width: '100%',
      // height: '100%',
      background: 'white',
      zIndex: '100',
      transition: 'opacity 1s',
      opacity: '1',
      textAlign: 'center',
      ...options,
    };
    const div = this.createDiv('instructionsPanelDiv', options);
    this.layout.instructionsPanel.div = this.instructionsPanelDiv;
    return div;
  }

  initializeElements(): void {
    debug('initializing elements');
    if (this.settings.instructions) this.initializeInstructions();
    if (this.settings.notation) this.initializeNotation();
    if (this.settings.piano) this.initializePiano();
    if (this.settings.editor) this.initializeEditor();
    if (this.settings.letters || this.settings.checkButton) this.initializeLetters();
    //      if(this.settings.accidentals) this.initializeAccidentals();
    if (this.settings.checkMark) this.initializeCheckMark();
    // if (this.settings.instructionsPanel) this.initializeInstructionsPanel();
    this.initializeCustomElements();
  }

  initializeCustomElements(): void {}

  initializeInstructions(): void {}

  initializeNotation(): void {
    if (typeof this.settings.notation !== 'object') return;
    if (!this.music) {
      throw Error('Cannot initialize notation until this.music is defined.');
    }
    if (!this.notationDiv) {
      throw Error('Cannot initialize notation unless this.notationDiv is defined.');
    }
    if (!this.layout.notation) {
      throw Error('Cannot initialize notation unless this.layout.notation is defined.');
    }
    this.notation = new Renderer(this.music, this.notationDiv, this.settings.notation);
    this.layout.notation.callback = this.notation.responsiveResize.bind(this.notation);
    this.layout.notation.visible = true;
  }

  initializePiano(): void {
    if (!this.pianoDiv) {
      throw Error('Cannot initialize piano unless this.pianoDiv is defined.');
    }
    if (!this.layout.piano) {
      throw Error('Cannot initialize piano unless this.layout.piano is defined.');
    }
    const options = typeof this.settings.piano === 'object' ? this.settings.piano : {};
    this.piano = new PianoSVG(this.pianoDiv, options);
    this.layout.piano.callback = this.piano.resize.bind(this.piano);
    this.layout.piano.visible = true;
    this.piano.removeSVGListeners();
    this.piano.removeListeners();
  }

  initializeEditor(): void {
    if (!this.music) {
      throw Error('Cannot initialize editor until this.music is defined.');
    }
    if (!this.editorDiv) {
      throw Error('Cannot initialize editor unless this.editorDiv is defined.');
    }
    if (!this.layout.editor) {
      throw Error('Cannot initialize editor unless this.layout.editor is defined.');
    }

    const options = typeof this.settings.editor === 'object' ? this.settings.editor : {};
    this.editor = new Editor(this.music, this.editorDiv, options);
    this.layout.editor.callback = this.editor.renderer.responsiveResize.bind(this.editor.renderer);
    this.layout.editor.visible = true;
  }

  initializeLetters(): void {
    if (!this.layout.letters) {
      throw Error('Cannot initialize letters unless this.layout.letters is defined.');
    }
    const letterSettings = this.settings.letters || {};
    utils.merge(letterSettings, this.settings.letters || {});
    if (this.optionsTrue('testMode') && (letterSettings as any).checkButton) {
      (letterSettings as any).checkButton = 'Submit';
    }

    this.letterPanel = new LetterPanel(this.lettersDiv!, letterSettings);
    this.layout.letters.callback = this.letterPanel.responsiveResize.bind(this.letterPanel);
    this.layout.letters.visible = true;
  }

  initializeInstructionsPanel(): void {
    if (!this.instructionsPanelDiv) {
      throw Error(
        'Cannot initialize instructions panel unless this.instructionsPanelDiv is defined.'
      );
    }
    if (!this.layout.instructionsPanel) {
      throw Error(
        'Cannot initialize instructionsPanel unless this.layout.instructionsPanel and this.layout.instructionsPanel.callback are defined.'
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const settings = {
      ariaLive: 'polite',
      ...(this.settings.instructionsPanel as InstructionsPanelOptions),
    };
    const div = this.instructionsPanelDiv;
    if (settings.ariaLive) {
      div.setAttribute('aria-live', settings.ariaLive);
    }
    const textDiv = document.createElement('div');
    textDiv.innerHTML = settings.innerHTML || '';
    div.appendChild(textDiv);
    const buttonDiv = document.createElement('div');
    const button = document.createElement('button');
    button.classList.add('uButton');
    button.setAttribute('id', 'StartButton');
    button.innerHTML = settings.buttonLabel || 'Start';
    div.appendChild(buttonDiv);
    textDiv.tabIndex = 0;
    textDiv.focus();

    // GCR WARN: Verify removing self worked.
    // const self = this;
    let height = this.getAvailableHeight() - 20;

    buttonDiv.innerHTML = 'loading...';
    if (settings.onSplashScreen) settings.onSplashScreen(textDiv, buttonDiv);
    else this.updateLoadProgress(buttonDiv);

    Promise.all([
      this.audioLoadingPromise,
      this.uPrefsLoadingPromise,
      this.questionDataLoadingPromise,
    ]).then(() => {
      if (this.settings.instructionsPanel && this.settings.instructionsPanel.onInteractiveReady) {
        this.settings.instructionsPanel.onInteractiveReady(textDiv, buttonDiv, button);
        this.sizeTextDiv(height * 0.7, textDiv);
      }
      this.showInstructionsPanelStartButton(button, buttonDiv, () => {
        height = this.getAvailableHeight() - 20;
        // Note that the default callback won't use either of these arguments, but we keep
        // them present in case it is overridden in an interactive
        this.layout.instructionsPanel!.callback!(height, this.layout.instructionsPanel!.div!);
        this.sizeTextDiv(height * 0.3, buttonDiv, { elToResize: button });
      });
    });

    const thisArg = settings.thisArg || this;
    let clicked = false;
    button.onclick = () => {
      // Prevent double click:
      if (clicked) return;
      clicked = true;
      button.disabled = true;
      this.fadeInstructionsPanel(button, div);
      // Callback anything that's been requested:
      if (settings.callback) {
        settings.callback.bind(thisArg)();
      }
      this.loadingPromise.then(() => this.startTimerAndListeners());
    };

    const resize = () => {
      height = this.getAvailableHeight() - 20;
      //        debug("Fitting to: " + height);
      this.sizeTextDiv(height * 0.7, textDiv);
      const arr = Array.prototype.slice.call(buttonDiv.children);
      const buttonInDiv = arr.indexOf(button) !== -1;
      if (buttonInDiv) {
        return this.sizeTextDiv(height * 0.3, buttonDiv, { elToResize: button });
      }
      return this.sizeTextDiv(height * 0.3, buttonDiv);
    };

    this.layout.instructionsPanel.visible = true;
    this.layout.instructionsPanel.overlay = true;
    this.layout.instructionsPanel.callback = resize;
  }

  updateLoadProgress(div: HTMLDivElement): void {
    const needAudio = !!this.settings.loadAudioFirst;
    const needUprefs = !!this.settings.uPrefs && !this.settings.uPrefs.optional;
    const needQuestionData = !!this.settings.questionData;

    if (!needAudio && !needUprefs && !needQuestionData) return;

    let toLoadString: string;
    if (needAudio && (needUprefs || needQuestionData)) {
      toLoadString = 'audio, exercise questions & history';
    } else if (needAudio) {
      toLoadString = 'audio';
    } else {
      toLoadString = 'exercise questions & history';
    }

    div.innerHTML = `Loading ${toLoadString}...<br />`;
    const p = document.createElement('progress');
    p.max = 100;
    p.value = 0;
    div.appendChild(p);

    const needAudioPercent = this.settings.loadAudioFirst ? 70 : 0;
    const dataProgressPercent = 15 * (Number(needQuestionData) + Number(needUprefs));

    let audioProgress = 0;
    let dataProgress = 0;
    const totalPercent = needAudioPercent + dataProgressPercent;

    const updateValue = () => {
      const value = ((audioProgress + dataProgress) * 100) / totalPercent;
      p.value = value;
    };

    this.uPrefsLoadingPromise.then(() => {
      dataProgress += needUprefs ? 15 : 0;
      updateValue();
    });
    this.questionDataLoadingPromise.then(() => {
      dataProgress += needQuestionData ? 15 : 0;
      updateValue();
    });

    if (this.settings.loadAudioFirst) {
      if (!this.player) {
        throw Error('Cannot update load progress until this.player is defined.');
      }
      const loadStatus = this.player.buffer.loadStatus();
      if (loadStatus < 100) {
        this.player.notifyLoadStatus((percent) => {
          audioProgress = percent;
          updateValue();
        }, this);
      }
    }
  }

  showInstructionsPanelStartButton(
    button: HTMLButtonElement,
    buttonDiv: HTMLDivElement,
    sizeButtonFunction?: Function
  ): void {
    // Override this to implement custom loading logic, etc...
    this.loadingPromise.then(() => {
      buttonDiv.innerHTML = '';
      buttonDiv.appendChild(button);
      if (sizeButtonFunction) sizeButtonFunction();
    });
  }

  startTimerAndListeners(): void {
    if (!this.progressTracker) {
      throw Error('Cannot start timer and listeners until this.progressTracker is defined.');
    }
    // Enable keyboard listeners & start the progress tracker:
    this.addListeners.bind(this)();
    if (this.settings.progressTracker) {
      this.progressTracker.start(this.targetSpeed);
    }
  }

  fadeInstructionsPanel(button: HTMLButtonElement, div = this.instructionsPanelDiv): void {
    // Called by default when start button is pressed on an
    // instructions panel -- but may be overridden in individual
    // interactive if a different behavior is desired.
    this.displayDiv.style.height = '';

    // Disable button
    button.onclick = () => {};
    button.disabled = true;

    if (!div || !this.layout.instructionsPanel) return;
    // Hide the panel:
    // eslint-disable-next-line no-param-reassign
    div.style.opacity = '0';
    this.layout.instructionsPanel.visible = false;
    // Let people click through it:
    // eslint-disable-next-line no-param-reassign
    div.style.pointerEvents = 'none';

    div.addEventListener('transitionend', () => {
      if (div && div.parentElement) {
        div.parentElement.removeChild(div);
      }
    });

    //
  }

  initializePlayer(): void {
    this.player = new PianoInstrument();
  }

  initializeCheckMark(): void {
    const checkSettings = this.settings.checkMark as CheckSettings;
    const checkDiv = checkSettings.div || this.displayDiv;
    this.checkMark = new CheckMark(checkDiv, checkSettings);
  }

  setTargetSpeed(targetSpeedFromSkills = this.targetSpeedFromSkills): number | undefined {
    if (this.targetSpeed) return undefined;
    if (!targetSpeedFromSkills) return undefined;
    if (!Array.isArray(targetSpeedFromSkills)) {
      targetSpeedFromSkills = [targetSpeedFromSkills];
    }
    this.targetSpeed = this.getTargetSpeedFromSkills(targetSpeedFromSkills);
    return this.targetSpeed;
  }

  getTargetSpeedFromSkills(skillIds: (string | number)[]) {
    if (!Array.isArray(skillIds)) {
      skillIds = [skillIds];
    }
    const speeds = skillIds
      .map(Skills.getSkill)
      .filter((s): s is Skill => !!s && !!s.master.speed)
      .map((skill) => (skill as Skill).master.speed)
      .filter(isNumber);
    if (!speeds.length) return undefined;
    return Math.min(...speeds);
  }

  // ## Question generation & display logic:

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  generateQuestions(questionStringOrArray?: any | any[]): void {
    // / Holder method to be overridden.

    // Generally, this sets four variables:
    // this.questions = [], an array of objects representing the questions & answers.
    //               .text : string representation for display
    //               .answer : answer object for comparison
    //               .skills : integer or array of integers of unique_id's for skills.
    // this.questionPrompt, the text displayed to the user, with {question} replaced by...
    // this.questionNumber = 0;
    this.questions = []; // prevents errors for interactives that don't override.
  }

  // If there will be a this.music object, this initializes it.
  initializeMusic(): void {}

  newQuestion(disableAudio?: boolean): void {
    this.wrongAnswerCount = 0;
    this.currentQuestion = this.questions[this.questionNumber];
    this.displayInstructions(disableAudio);
    if (this.progressTracker && !this.settings.instructionsPanel && this.questionNumber === 0) {
      this.progressTracker.start(this.targetSpeed);
    }
    // log it to Rollbar if we can to make debugging easy.
    if ((window as any).Rollbar) {
      interface QuestionEvent {
        type: string;
        questionNumber: number;
        question?: string;
      }
      const newQuestionEvent: QuestionEvent = {
        type: 'new question',
        questionNumber: this.questionNumber,
      };

      let question = '';
      try {
        question = JSON.stringify(this.currentQuestion);
        // eslint-disable-next-line no-empty
      } catch (e) {}
      if (question) newQuestionEvent.question = question;

      (window as any).Rollbar.captureEvent(newQuestionEvent, 'info');
    }
    this.rightAnswerDelay = false;
  }

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  displayInstructions(disableAudio?: boolean): void {
    const height = this.instructionsDivHeight || this.instructionsDiv!.clientHeight;
    const instructionsOptions: InstructionsOptions = {
      ariaLive: 'off',
      ariaLiveRegion: 'all',
    };
    if (this.settings.instructions && typeof this.settings.instructions !== 'boolean') {
      instructionsOptions.ariaLive = this.settings.instructions.ariaLive;
      instructionsOptions.ariaLiveTitle = this.settings.instructions.ariaLiveTitle;
      instructionsOptions.ariaLiveRegion = this.settings.instructions.ariaLiveRegion;
    }

    let questionText = this.currentQuestion.text;
    if (!(questionText instanceof Array)) questionText = [questionText];

    let questionPrompt = '';
    if (typeof this.questionPrompt === 'function') {
      questionPrompt = this.questionPrompt(questionText);
    } else {
      questionPrompt = this.questionPrompt || questionPrompt;
    }

    const { innerHTML } = displayInstructions(
      this.instructionsDiv!,
      questionPrompt,
      questionText,
      instructionsOptions,
      this.priorInstructions
    );

    this.priorInstructions = {
      questionPrompt,
      questionText,
    };
    this.currentInstructions = innerHTML;
    const noWrap = typeof this.layout.instructions === 'object' && this.layout.instructions.noWrap;
    this.sizeTextDiv(height, this.instructionsDiv!, { noWrap });
  }

  // Adjusts & resizes the instructions to preserve its size:
  updateInstructions(innerHTML: string | HTMLElement): void {
    if (!this.instructionsDiv) {
      throw Error('Cannot update instructions if this.instructionsDiv is undefined.');
    }
    const height = this.instructionsDivHeight || this.instructionsDiv.clientHeight;
    if (typeof innerHTML === 'string') {
      this.instructionsDiv.innerHTML = innerHTML;
    } else {
      this.instructionsDiv.innerHTML = '';
      this.instructionsDiv.appendChild(innerHTML);
    }
    const noWrap = typeof this.layout.instructions === 'object' && this.layout.instructions.noWrap;
    this.sizeTextDiv(height, this.instructionsDiv, { noWrap });
  }

  // ## Callback functions:
  addListeners(): void {
    // Code to set callbacks & communication to eventually check an answer.
  }

  addGameOverCallback(callback: GameOverCallback, thisArg?: any): void {
    if (typeof thisArg !== 'undefined') {
      this.gameOverCallbacks.push(callback.bind(thisArg));
    } else this.gameOverCallbacks.push(callback);
  }

  gameOver(dontSendData: boolean = false) {
    this.debug('Game over: ', this.constructor.name);
    if (this.instructionsDiv) {
      this.instructionsDiv.innerHTML = 'Nice work!';
      this.instructionsDiv.setAttribute('role', 'alert');
    }

    if (this.piano) {
      this.piano.destroy();
    }

    if (typeof this.lettersDiv !== 'undefined') {
      this.lettersDiv.style.display = 'none';
    }
    if (mousetrapLastResetBy === this) {
      Mousetrap.reset();
    }
    const data = this.progressTracker ? this.progressTracker.getData() : undefined;

    if (!dontSendData) {
      this.gameOverCallbacks.forEach((callback) => {
        callback(data);
      }, this);
    }

    if (this.editor) {
      this.editor.deactivate();
    }
  }

  exit() {
    this.debug('Exit: ', this.constructor.name);
    // remove resize listener:
    utils.clearResize(this.resizeIndex);
    // remove keyboard listeners:
    if (mousetrapLastResetBy === this) {
      Mousetrap.reset();
    }

    // remove Midi listeners:
    if (this.piano) {
      this.piano.destroy();
    }
    // remove load status notifiers on sound buffer:
    if (this.player && this.player.buffer) {
      this.player.buffer.clearNotifiers();
    }
  }

  addRightAnswerCallback(callback: (gameOver?: boolean) => void, thisArg?: any): void {
    if (typeof thisArg !== 'undefined') {
      this.rightAnswerCallbacks.push(callback.bind(thisArg));
    } else this.rightAnswerCallbacks.push(callback);
  }

  addWrongAnswerCallback(callback: (gameOver?: boolean) => void, thisArg?: any): void {
    if (typeof thisArg !== 'undefined') {
      this.wrongAnswerCallbacks.push(callback.bind(thisArg));
    } else this.wrongAnswerCallbacks.push(callback);
  }

  // ## Checking answer functions:

  check(answer: any): void {
    // eslint-disable-next-line eqeqeq
    if (this.questions[this.questionNumber].answer === answer) {
      this.rightAnswer();
    } else {
      this.wrongAnswer();
    }
  }

  gameIsOver(rightAnswer: boolean): boolean {
    const incrementQuestion = rightAnswer || this.settings.oneTryOnly ? 1 : 0;
    return this.questionNumber + incrementQuestion >= this.questions.length;
  }

  /*  delay in MS
   *  options = {}
   *    skills: [],  // replace skills in questionNumber, useful for
   *                 // multi-part questions, etc...
   *    noPlay: bool // if true, answer is not played.
   */

  /**
   * call when a user gets a right answer
   * @param delay -  time in MS to wait before showing next question
   * @param options -
   *   options.noPlay - if true, answer is not played
   *   options.skills - replace skills in questionNumber, useful for
   *     multi-part questions, etc...
   *   options.noProgressTracke -  if true, don't call progress tracker.
   *   options.noPlay - if true, don't play the answer.
   */
  rightAnswer(
    delay?: number,
    options: { noPlay?: boolean; skills?: (string | number)[]; noProgressTracker?: boolean } = {}
  ): void {
    const gameOver = this.gameIsOver(true);

    const skills = options.skills || this.currentQuestion.skills;
    if (this.progressTracker && !options.noProgressTracker) {
      this.progressTracker.rightAnswer(skills, gameOver);
    }
    if (!options.noPlay) this.playAnswer();
    // Determine whether the game is over:
    this.questionNumber++;

    // Notify student of right answer, log & track it all:
    if (this.checkMark) this.checkMark.check();
    this.rightAnswerCallbacks.forEach((callback) => {
      callback(gameOver);
    }, this);

    // If the game's not over, go on to the next question.
    if (typeof delay !== 'undefined') {
      this.rightAnswerDelay = true;
      window.setTimeout(() => {
        if (gameOver) this.gameOver();
        else this.newQuestion();
      }, delay);
    } else if (gameOver) this.gameOver();
    else this.newQuestion();
  }

  /* @text is shown after x-mark
   * @options -- see rightAnswer
   * @param {Object} options.skills
   *
   */
  /**
   *
   * @param warningText -  warning message to student when wrong
   * @param options -
   * @param skills -  filtered list of skills from the current question to mark as
   * incorrect. If not included, all current question skills are marked wrong.
   */
  wrongAnswer(
    warningText?: string,
    options: {
      noPlay?: boolean;
      skills?: (string | number)[];
      noProgressTracker?: boolean;
      alternateAriaText?: string;
    } = {}
  ): void {
    const gameOver = this.gameIsOver(false);

    const skills = options.skills || this.currentQuestion.skills;
    if (this.progressTracker && !options.noProgressTracker) {
      this.progressTracker.wrongAnswer(skills, gameOver);
    }
    // Determine whether the game is over:
    if (this.settings.oneTryOnly) this.questionNumber++;

    // Notify student of wrong answer:
    if (this.checkMark) {
      if (typeof warningText !== 'undefined') {
        this.checkMark.xAndWarn(warningText);
      } else {
        this.checkMark.x(undefined, options.alternateAriaText);
      }
    }
    this.wrongAnswerCallbacks.forEach((callback) => {
      callback(gameOver);
    }, this);

    if (gameOver) this.gameOver();
    else if (this.settings.oneTryOnly) {
      this.newQuestion();
    }
    //
  }

  debugRightAnswer(): void {
    this.rightAnswer();
  }

  debugWrongAnswer(): void {
    this.wrongAnswer();
  }

  playAnswer(): void {
    if (this.music && this.player) this.player.playMusic(this.music, { tempo: this.tempo });
  }

  optionsTrue(optionName: string): boolean {
    return (
      typeof this.options[optionName as keyof InteractiveOptions] !== 'undefined' &&
      this.options[optionName as keyof InteractiveOptions] === true
    );
  }

  optionSet(optionName: string): boolean {
    return typeof this.options[optionName as keyof InteractiveOptions] !== 'undefined';
  }

  settingSet(settingName: string): boolean {
    return (
      typeof this.settings[settingName as keyof InteractiveSettings] !== 'undefined' &&
      this.settings[settingName as keyof InteractiveSettings] !== false
    );
  }

  // ### Checkpoint Assessment Functions:
  // These are used when the module is initialized in testMode.
  // By default we assume generateQuestions can handle it,
  // but if not we can override.
  newCheckpointQuestion(questionString: any | any[], disableAudio?: boolean): Promise<any> {
    return this.loadingPromise.then(() => {
      this.generateQuestions([].concat(questionString));
      this.newQuestion(disableAudio);
      return Promise.resolve(this.currentQuestion);
    });
  }

  // Checks the current question, and returns a data set
  // based on however the student answered.
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  getAnswerAndData(answerIsCorrect?: any): AnswerAndData {
    return {
      question: 'C Maj/treble', // What's passed to the interactive to initialize a question
      questionType: 'ChordId', // GameName
      questionText: 'Draw C Major', // human readable question: "What note is this?"
      // questionSVG?: string,
      studentAnswerText: 'C4-E4-A4', // student's attempted answer; may match 'question' or 'correctAnswerText'
      // studentAnswerSVG?: string,
      correctAnswerText: 'C4-E4-G4',
      rightAnswers: 0, // number
      wrongAnswers: 1, // number
      skills: ['spelling_triads', 'major_triads'], // skill Ids
    };
  }

  // For modules that don't require a submit button
  addAnswerCallback(callback: AnswerAndDataCallback, thisArg?: any): void {
    if (typeof thisArg !== 'undefined') {
      this.checkpointAnswerCallbacks.push(callback.bind(thisArg));
    } else this.checkpointAnswerCallbacks.push(callback);
  }

  // The module calls this when an answer is made and no submit
  // button is required.
  checkpointAnswer(submittedAnswer?: any): void {
    const answerAndData = this.getAnswerAndData(submittedAnswer);
    this.checkpointAnswerCallbacks.forEach((callback) => {
      callback(answerAndData);
    }, this);
  }
}
